// Shared helpers for the Day-2 Modification custom fields. These compute what
// changed between the original form snapshot (`_original`, injected by the
// Modify wizard) and the current form values, so the fields can warn about and
// gate destructive actions.

export interface Day2Form {
  _original?: Record<string, any>;
  environments?: string[];
  enable_static_web?: boolean;
  enable_app_service?: boolean;
  enable_container_app_jobs?: boolean;
  enable_postgres?: boolean;
  enable_object_storage?: boolean;
  enable_key_vault?: boolean;
  delete_project?: boolean;
  [key: string]: any;
}

/** True when a boolean toggle was on originally and is now off. */
export function turnedOff(
  original: Record<string, any> | undefined,
  current: Record<string, any>,
  key: string,
): boolean {
  return !!original?.[key] && !current?.[key];
}

/** Environments present in the original snapshot but removed in the current form. */
export function removedEnvironments(
  original: Record<string, any> | undefined,
  current: Record<string, any>,
): string[] {
  const before: string[] = Array.isArray(original?.environments)
    ? original!.environments
    : [];
  const after: string[] = Array.isArray(current?.environments)
    ? current.environments
    : [];
  return before.filter(env => !after.includes(env));
}

/** True when the form asks for the whole application to be deleted. */
export function isProjectDeletion(current: Record<string, any>): boolean {
  return current?.delete_project === true;
}

/**
 * The name every resource is actually named after. Mirrors
 * `normalizeStarterAppName().app_name_input` in the backend: accents stripped,
 * lower-cased, everything but [a-z0-9] removed. The raw form value ("My App")
 * is what the user typed on Day-1; `vg-My App` does not exist, `vg-myapp` does.
 */
export function normalizeAppName(value: string): string {
  return value
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
}

/**
 * What a project deletion destroys, in the user's words. Mirrors
 * `projectDeletionActions` in the backend's day2Plan action, which is what the
 * PR description and the email say — the two lists must not disagree.
 */
export function projectDeletionActions(
  appName: string,
  environments: string[],
): string[] {
  const envs =
    environments.length > 0 ? environments.join(', ') : 'every environment';
  return [
    `Every Azure resource of "${appName}" in ${envs} will be destroyed — databases, storage and Key Vault secrets included.`,
    `Each environment's Crayon subscription will be cancelled and its service principal (sp-alz-${appName}-<env>-<region>) deleted.`,
    `The service connections sp-alz-${appName}-<env>-<region>-sc and -acrpull, and their federated credentials, will be removed from Azure DevOps.`,
    `The variable group vg-${appName} will be deleted once the last environment is torn down.`,
    `The Grafana dashboards ${appName}-<env> that Backstage created will be deleted, and the Grafana folder ${appName} once nothing else is left in it.`,
    `The Auth0 application ${appName} that Backstage created, if any, will be deleted from each environment's Auth0 tenant, with its API and client grants, once every decommission PR is merged. Only the production tenants ask for an approval first.`,
    `The application's own CI/CD pipelines are NOT removed: they will fail once vg-${appName} is gone and must be deleted by hand.`,
    'Source repositories are kept. No Azure resource is destroyed until each decommission PR is merged and its destroy plan is approved in the infrastructure pipeline.',
  ];
}

/**
 * Human-readable list of destructive actions implied by the current changes.
 * Used both for the inline warnings and for the review-page confirmation gate.
 *
 * A project deletion replaces the list outright: every other edit on the form
 * is ignored by the run, so listing it here would describe something that is
 * not going to happen.
 */
export function computeDestructiveActions(
  original: Record<string, any> | undefined,
  current: Record<string, any>,
): string[] {
  if (!original) return [];

  if (isProjectDeletion(current)) {
    const environments: string[] = Array.isArray(original.environments)
      ? original.environments
      : [];
    const appName = normalizeAppName(
      String(current.app_name ?? original.app_name ?? ''),
    );
    return projectDeletionActions(appName || 'this application', environments);
  }

  const actions: string[] = [];

  if (turnedOff(original, current, 'enable_postgres')) {
    actions.push(
      'Database (Postgres) will be destroyed — all data inside will be permanently lost.',
    );
  }
  if (turnedOff(original, current, 'enable_object_storage')) {
    actions.push(
      'Object storage will be destroyed — all files inside will be permanently lost.',
    );
  }
  if (turnedOff(original, current, 'enable_key_vault')) {
    actions.push(
      'Key Vault will be destroyed — the secrets stored in it will be permanently lost.',
    );
  }
  // Turning a capability off destroys its Azure infrastructure only. The source
  // repository is always KEPT, so the code and its history survive and the
  // capability can be switched back on later.
  if (turnedOff(original, current, 'enable_app_service')) {
    actions.push(
      'The API service infrastructure will be destroyed. The repository is kept.',
    );
  }
  if (turnedOff(original, current, 'enable_static_web')) {
    actions.push(
      'The web frontend infrastructure will be destroyed. The repository is kept.',
    );
  }
  if (turnedOff(original, current, 'enable_container_app_jobs')) {
    actions.push(
      'The jobs infrastructure will be destroyed. The repository is kept.',
    );
  }
  for (const env of removedEnvironments(original, current)) {
    actions.push(
      `Environment "${env}" will be torn down — every Azure resource in it is destroyed. ` +
        `Submitting only opens a pull request; the destroy itself runs in the infrastructure ` +
        `pipeline after that PR is merged, and a human must approve the destroy plan first.`,
    );
  }

  return actions;
}
